import React, { createContext, useState, useEffect, useContext } from "react";
import { createClient } from "@supabase/supabase-js";

// Initialize Supabase client using environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check that environment variables are set
if (!supabaseUrl || !supabaseKey) {
  console.error(
    "Supabase URL or API key is missing. Make sure to set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file."
  );
}

const supabase = createClient(supabaseUrl, supabaseKey);

// Create authentication context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Check for user session on mount
  useEffect(() => {
    const checkUser = async () => {
      try {
        setLoading(true);

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          // Get user profile data from the users table
          const { data: userData, error } = await supabase
            .from("users")
            .select("*")
            .eq("id", session.user.id)
            .single();

          if (userData) {
            setUser({
              ...session.user,
              ...userData,
            });
          } else {
            // If no user profile, just use the session user
            setUser(session.user);
          }
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setAuthError(error.message);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Set up listener for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // Fetch updated user data when auth state changes
        const fetchUserProfile = async () => {
          const { data, error } = await supabase
            .from("users")
            .select("*")
            .eq("id", session.user.id)
            .single();

          if (data) {
            setUser({
              ...session.user,
              ...data,
            });
          } else {
            setUser(session.user);
          }
        };

        fetchUserProfile();
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Simplified signup function
  const signUp = async (email, password, username, organization) => {
    try {
      setAuthError(null);

      // Step 1: Sign up with Supabase Auth and wait for the session
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) {
        throw authError;
      }

      console.log("Auth signup successful, user id:", authData.user?.id);

      // Step 2: After successful auth signup, try to create the profile
      if (authData.user) {
        // First, let's sign in to make sure we have a valid session
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

        console.log("Signed in to create profile");

        // Now try to create the profile with a valid session
        const { error: profileError } = await supabase.from("users").insert({
          id: authData.user.id,
          username,
          email,
          organization: organization === "na" ? null : organization,
          unit_1_progress: 0,
          unit_2_progress: 0,
          unit_3_progress: 0,
          unit_4_progress: 0,
          created_at: new Date().toISOString(),
        });

        if (profileError) {
          console.error("Profile creation error:", profileError);
          // Even if profile creation fails, we'll return the auth data
          // as the user has been created in Supabase Auth
        } else {
          console.log("Profile created successfully");
        }
      }

      return authData;
    } catch (error) {
      console.error("Error in signup process:", error);
      setAuthError(error.message);
      throw error;
    }
  };

  // Sign in function
  const signIn = async (email, password) => {
    try {
      setAuthError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // After successful sign-in, fetch and set user profile data
      if (data.user) {
        const { data: userData, error: profileError } = await supabase
          .from("users")
          .select("*")
          .eq("id", data.user.id)
          .single();

        if (userData && !profileError) {
          // Update the user state with combined auth and profile data
          setUser({
            ...data.user,
            ...userData,
          });
        } else {
          // If we can't fetch profile, at least set the auth user data
          setUser(data.user);
        }
      }

      return data;
    } catch (error) {
      console.error("Error signing in:", error);
      setAuthError(error.message);
      throw error;
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
      setAuthError(error.message);
      throw error;
    }
  };

  // Update user data function (unit progress)
  const updateUserData = async (updates) => {
    try {
      if (!user) throw new Error("No user logged in");

      const { data, error } = await supabase
        .from("users")
        .update(updates)
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Update local user state with new data
      setUser({
        ...user,
        ...data,
      });

      return data;
    } catch (error) {
      console.error("Error updating user data:", error);
      throw error;
    }
  };

  // Context value
  const value = {
    user,
    loading,
    authError,
    signUp,
    signIn,
    signOut,
    updateUserData,
    supabase, // Include supabase client in case it's needed elsewhere
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
